<template>
  <div>
    <commonModal
      :show="showErrorModal"
      :actionButton="true"
      :options="{
        title: 'Sorry!',
        description: `We were unable to locate your quote using that information. To try again, please close this window, or you may click the button below to begin a new quote.`,
        buttonLabel: 'New Quote'
      }"
      @close="clickedCloseOnModal"
    ></commonModal>
    <div class="header-image">
      <img
        :src="require('@/assets/img/VM24_6470.jpg')"
        alt="Contact Porsche Auto Insurance"
      />
    </div>
    <section class="container">
      <div class="policy-docs form">
        <h1 class="title">Request Documents & Insurance Card</h1>
        <p>
          To receive your Porsche Auto Insurance documents electronically, enter
          the email address associated with your policy in the box below and
          click "Submit." If the email address provided below matches an active
          Porsche Auto Insurance policy, a secure link to view or download the
          policy documents will be sent to that email address.
        </p>
        <div class="policy-docs fields">
          <label class="input -spacing-static-c">
            <input
              v-model="retrievePolicyForm.emailAddress"
              name="emailAddress1"
              :class="{
                input__field: true,
                error: errors.has('emailAddress1')
              }"
              placeholder="Email Address"
              v-validate="'required|email'"
            />
            <span class="input__label">Email Address</span>
          </label>
          <div class="input-container">
            <button
              type="button"
              v-bind:disabled="errors.has('emailAddress1')"
              @click="sendPolicyDocsClick"
              :class="{
                'button-primary': true,
                ' button-primary--black': true,
                'button-primary--homepage': true,
                'is-loading': sendLoad
              }"
            >
              Submit
            </button>

            <b-icon
              pack="fas"
              icon="sync-alt"
              :class="{
                'is-loading': sendLoad,
                'fa-spin': true,
                'd-none': true
              }"
            ></b-icon>
            <b-icon
              pack="fas"
              icon="check"
              :class="{
                'is-loaded': docsLoaded,
                'd-none': true
              }"
            ></b-icon>
          </div>
        </div>
        <h2 class="subtitle">Need something else?</h2>
        <p>
          Our Customer Concierge team is available Monday through Friday from
          9am to 5pm to help you submit a quote, change a policy, or anything
          else you might need.
        </p>
        <p>
          You can reach us at
          <span style="white-space: nowrap;"
            ><a :href="'tel:1-' + $phoneNumber">{{ $phoneNumber }}</a></span
          >
          or email us at
          <span style="white-space: nowrap;"
            ><a href="mailto:customercare@porscheautoinsurance.com"
              >customercare@porscheautoinsurance.com</a
            ></span
          >
        </p>
      </div>
      <hr />
      <div class="quote-retrieval form">
        <h1 class="title">Retrieve Your Quote</h1>
        <p>
          To resume a quote you've already begun, please fill out the form
          below.
        </p>
        <div class="retrieve-quote fields">
          <label class="input -spacing-static-c">
            <input
              v-model="retrieveQuoteForm.emailAddress"
              name="emailAddress"
              :class="{
                input__field: true,
                error: errors.has('emailAddress')
              }"
              placeholder="Email Address"
              v-validate="'required|email'"
            />
            <span class="input__label">Email Address</span>
          </label>
          <div class="limit-container">
            <label class="input -spacing-static-c">
              <masked-input
                :class="{
                  input__field: true,
                  error: errors.has('dateOfBirth')
                }"
                type="text"
                name="dateOfBirth"
                placeholder="Date of birth (MM/DD/YYYY)"
                v-model="retrieveQuoteForm.dateOfBirth"
                @blur="
                  checkWorking('dateOfBirth', retrieveQuoteForm.dateOfBirth)
                "
                :keepCharPositions="true"
                v-validate="'required|validate18YrsOld|isLeapYear'"
                :placeholderChar="'_'"
                :pipe="autoCorrectedDatePipe"
                :mask="[
                  /\d/,
                  /\d/,
                  '/',
                  /\d/,
                  /\d/,
                  '/',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/
                ]"
                :guide="true"
              ></masked-input>
              <span class="input__label">Date of birth (MM/DD/YYYY)</span>
            </label>
            <span class="icon calendar-icon">
              <vue-fontawesome :icon="['fas', 'calendar']" />
            </span>
            <b-datepicker
              name="dateOfBirth1"
              @input="changedDOB('retrieveQuoteForm')"
              v-model="retrieveQuoteForm.selectedDate"
              :max-date="validationOptions.atleastEighteenYrsOld"
              :focused-date="validationOptions.atleastEighteenYrsOld"
              :mobile-native="false"
              data-vv-validate-on="input|blur"
            ></b-datepicker>
          </div>
          <label class="input -spacing-f">
            <input
              v-model="retrieveQuoteForm.zipCode"
              name="zipcode"
              maxlength="5"
              @paste="onPaste"
              @input="onlyNumbers"
              type="number"
              :class="{
                input__field: true,
                error: errors.has('zipcode')
              }"
              placeholder="ZIP Code"
              v-validate="'required|numeric|length:5'"
              data-vv-validate-on="input|blur"
            />

            <span class="input__label">ZIP Code</span>
          </label>
          <button
            type="button"
            v-bind:disabled="errors.any()"
            @click="retrieveQuoteClick"
            :class="{
              'button-primary': true,
              ' button-primary--black': true,
              'button-primary--homepage': true,
              'is-loading': retrieveLoad
            }"
          >
            Retrieve
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import moment from 'moment';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import commonModal from '@/components/shared/commonModal';
import FNOL from '@/components/account/FNOL.vue';

export default {
  name: 'MyPolicy',
  components: { FNOL, commonModal },
  data: () => ({
    retrieveLoad: false,
    sendLoad: false,
    docsLoaded: false,
    quoteLoaded: false,
    showErrorModal: false,
    retrievePolicyForm: {
      emailAddress: null
    },
    retrieveQuoteForm: {
      retrieveEmailAddress: null,
      zipCode: null,
      dateOfBirth: null,
      selectedDate: null
    },
    validationOptions: {
      dateOfBirth: {
        date: true,
        datePattern: ['m', 'd', 'Y'],
        delimiter: '/'
      },
      atleastEighteenYrsOld: new Date(
        new Date().setFullYear(new Date().getFullYear() - 18)
      )
    }
  }),
  computed: {
    autoCorrectedDatePipe() {
      return createAutoCorrectedDatePipe('mm/dd/yyyy');
    }
  },
  methods: {
    ...mapActions(['fetchQuote', 'requestPolicyDocs']),
    ...mapMutations(['toggleLoader']),
    changedDOB(target, event) {
      this[target].dateOfBirth = moment(event).format('MM/DD/YYYY');
      this.selectedDate = null;
      this.$el.querySelector('[name="dateOfBirth"]').focus();
      this.$validator.validate('dateOfBirth', this[target].dateOfBirth);
    },
    checkWorking(key, value) {
      let validator = this.$validator;
      validator.validate(key, value);
    },
    clickedCloseOnModal(value) {
      this.showErrorModal = false;
      this.toggleLoader();
      if (value === 1) {
        this.$router.push({
          path: 'receive-a-quote',
          query: { ...this.$route.query }
        });
      }
    },
    retrieveQuoteClick() {
      this.retrieveLoad = true;
      if (
        this.retrieveQuoteForm.emailAddress &&
        this.retrieveQuoteForm.dateOfBirth &&
        this.retrieveQuoteForm.zipCode
      ) {
        this.toggleLoader({
          loaderText: 'Please wait while we retrieve your information.'
        });
        const request = {
          emailAddress: this.retrieveQuoteForm.emailAddress,
          dob: moment(this.retrieveQuoteForm.dateOfBirth, 'MM/DD/YYYY').format(
            'YYYY-MM-DD'
          ),
          zip: this.retrieveQuoteForm.zipCode
        };
        this.fetchQuote(request)
          .then(() => {
            this.retrieveLoad = false;
            this.toggleLoader();
            this.$router.push({
              path: '/receive-a-quote',
              query: {
                ...this.$route.query,
                email: this.retrieveQuoteForm.emailAddress.toLowerCase(),
                dob: moment(
                  this.retrieveQuoteForm.dateOfBirth,
                  'MM/DD/YYYY'
                ).format('YYYY-MM-DD'),
                zipcode: this.retrieveQuoteForm.zipCode
              }
            });
          })
          .catch(() => {
            this.retrieveLoad = false;
            this.toggleLoader();
            this.showErrorModal = !this.showErrorModal;
          });
      } else {
        this.retrieveLoad = false;
        this.toggleLoader();
        this.$snackbar.open({
          type: 'is-danger',
          message: 'unable to load quote',
          position: 'is-bottom',
          indefinite: true
        });
      }
    },
    sendPolicyDocsClick() {
      this.sendLoad = true;
      this.toggleLoader({
        loaderText: 'Please wait while we retrieve your information.'
      });
      //  /documents/access endpoint.
      this.requestPolicyDocs(this.retrievePolicyForm)
        .then(res => {
          this.sendLoad = false;
          this.docsLoaded = true;
          this.toggleLoader();
          return res;
        })
        .catch(err => {
          this.toggleLoader();
          const message =
            (err && err.error_messages && err.error_messages[0]) ||
            'Cannot locate policy - please call Customer Care (Contact details below).';
          this.sendLoad = false;
          this.$snackbar.open({
            type: 'is-danger',
            message: message,
            position: 'is-bottom',
            indefinite: true
          });
        });
    },
    onPaste(event) {
      event.clipboardData.getData('text/plain').slice(0, 5);
    },
    onlyNumbers() {
      this.retrieveQuoteForm.zipCode = this.retrieveQuoteForm.zipCode
        .toString()
        .replace(/[^0-9]/g, '');
    }
  }
};
</script>
<style lang="scss" scoped>
@import 'src/assets/sass/modules/form/input.scss';
@import 'src/assets/sass/modules/form/datepicker.scss';
div.header-image {
  img {
    display: block;
    margin: 0 0 2rem 0;
    max-width: 100%;
    padding: 0;
  }
}
.input-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
span.icon {
  color: $primary;
}
.is-loading,
.is-loaded {
  color: $primary;
  display: block !important;
}
.d-none {
  display: none;
}
.container {
  padding-top: 1.5em;
  @media screen and (min-width: 700px) {
    padding-top: 0;
  }
}
button.button-primary {
  margin: 1em 1em 1em 0;
}
.form {
  margin: 0 2%;
  align-items: flex-start;
  justify-content: flex-start;
  width: 80%;
}
.fields {
  //margin: auto;
  margin-bottom: 1rem;

  .input {
    height: auto;
  }

  .input__label {
    opacity: 0;
  }

  .input__field {
    &:focus + .input__label {
      top: 30%;
      opacity: 0.5;
    }
    .input__label {
      opacity: 0;
    }

    @media screen and (min-width: 992px) {
      max-height: 3.75rem;
    }
  }
}
@media screen and (min-width: 992px) {
  input.input__field:focus {
    padding-top: 1.75rem;
    padding-bottom: 1rem;
  }
  .has-placeholders .input__field:not(:placeholder-shown) + .input__label,
  .has-placeholders .input__field:focus + .input__label {
    top: 2.75rem;
  }
}
@media screen and (min-width: 480px) {
  .has-placeholders .input__field + .input__label,
  .has-placeholders .input__field:focus + .input__label {
    top: 2.5rem;
  }
}
h1.title {
  text-align: center;
  font-family: $family-sans-serif;
  font-weight: bold;
  font-size: 2rem;
  line-height: 1.25em;
}
h2.subtitle {
  font-size: 1.5rem;
  line-height: 1.75rem;
}
h5.subtitle {
  font-size: 1.25em;
}
@media screen and (min-width: 1088px) {
  .fields {
    max-width: 50%;
  }
}
@media screen and (min-width: 1760px) {
  .fields {
    max-width: 70%;
  }
  .hero {
    hr {
      max-width: 50%;
    }
  }
}
</style>
